import { get, groupBy, pick, orderBy } from 'lodash';

import Api from '../../services/Api';

const upsertWebhookRequest = {
  title: 'upsertWebhookRequest',
  type: 'object',
  additionalProperties: false,
  required: [
    'url',
    'basicAuth',
    'oauth2ClientCredentials',
    'batchPayloadSize',
    'batchMaxWaitTime',
    'events',
    'messageTtl',
  ],
  properties: {
    url: {
      format: 'url',
      type: 'string',
      errorMessage: {
        format: 'Entered url is invalid',
      },
    },
    batchPayloadSize: {
      type: 'integer',
      enum: [32768, 131072, 1048576, 2097152, 4194304],
      errorMessage: {
        enum: 'Batch payload size should be equal to one of the allowed values',
      },
    },
    batchMaxWaitTime: {
      type: 'integer',
      enum: [5, 30, 60, 300],
      errorMessage: {
        enum:
          'Batch max wait time should be equal to one of the allowed values',
      },
    },
    messageTtl: {
      type: 'integer',
      enum: [14400, 86400, 604800, 2419200],
      errorMessage: {
        enum: 'Message TTL should be equal to one of the allowed values',
      },
    },
    events: {
      type: 'object',
      subscribedAtleastToOneEvent: true,
      subscribedToOnlyOneTransport: true,
      properties: {
        location: {
          type: 'boolean',
        },
        predictions: {
          type: 'boolean',
        },
        stationaries: {
          type: 'boolean',
        },
        moments: {
          type: 'boolean',
        },
        all_transports: {
          type: 'boolean',
        },
        car_transports: {
          type: 'boolean',
        },
      },
    },
    authMethod: {
      type: 'string',
      enum: ['basic_auth', 'oauth2_client_credentials']
    },
    basicAuth: {
      type: 'object',
      additionalProperties: false,
      required: [
        'username',
        'password'
      ],
      properties: {
        username: {
          type: 'string',
          minLength: 5,
          errorMessage: {
            minLength: 'Username should have at least 5 characters',
          },
        },
        password: {
          type: 'string',
          minLength: 5,
          errorMessage: {
            minLength: 'Password should have at least 5 characters',
          },
        },
      }
    },
    oauth2ClientCredentials: {
      type: 'object',
      additionalProperties: false,
      required: [
        'clientId',
        'clientSecret',
        'scope',
        'authorizationEndpoint'
      ],
      properties: {
        clientId: {
          type: 'string',
          minLength: 5,
          errorMessage: {
            minLength: 'Client ID should have at least 5 characters',
          },
        },
        clientSecret: {
          type: 'string',
          minLength: 5,
          errorMessage: {
            minLength: 'Client secret should have at least 5 characters',
          },
        },
        scope: {
          type: 'string',
          minLength: 1,
          errorMessage: {
            minLength: 'Scope should not be empty',
          },
        },
        authorizationEndpoint: {
          format: 'url',
          type: 'string',
          errorMessage: {
            format: 'Entered authorization endpoint is invalid',
          },
        },
      }
    },
  },
  errorMessages: {
    enum: 'Should be equal to one of the allowed values',
    format: 'Please enter a proper url',
  },
};

function resetRequestFormErrors() {
  return {
    url: null,
    batchMaxWaitTime: null,
    batchPayloadSize: null,
    messageTtl: null,
    basicAuth: {
      username: null,
      password: null,
    },
    oauth2ClientCredentials: {
      clientId: null,
      clientSecret: null,
      scope: null,
      authorizationEndpoint: null
    }
  };
}

function reset() {
  return {
    currentWebhookId: null,
    activeWebhook: null,
    requestedWebhook: null,
    validationActions: [],
    customMessageTypes: [],
    errors: [],
    webhookFormValidator: upsertWebhookRequest,
    requestForm: {
      events: {
        locations: false,
        car_transports: false,
        predictions: false,
        moments: false,
        all_transports: false,
        stationaries: false,
      },
      url: '',
      authMethod: 'basic_auth',
      basicAuth: {
        username: '',
        password: '',
      },
      oauth2ClientCredentials: {
        clientId: '',
        clientSecret: '',
        scope: '',
        authorizationEndpoint: '',
      },
      batchPayloadSize: 32768,
      batchMaxWaitTime: 5,
      messageTtl: 86400,
    },
    requestFormErrors: resetRequestFormErrors(),
  };
}
const state = reset();

const actions = {
  async getCustomMessageTypes({ commit }) {
    const response = await Api.ganderPortal.get(
      `apps/${state.currentWebhookId}/custom_message_types`
    );

    commit('setCustomMessageTypes', response.body);
  },

  async getWebhooksForAppId({ commit }, appId) {
    const response = await Api.ganderPortal.get(
      `apps/${appId}/webhook_requests`
    );
    const groupedResponse = groupBy(response.body, 'is_activated');
    const activeWebhooks = orderBy(
      groupedResponse['true'],
      ['updated_at', 'created_at'],
      ['desc', 'desc']
    );
    const unapprovedWebhooks = orderBy(
      groupedResponse['null'],
      ['updated_at', 'created_at'],
      ['desc', 'desc']
    );

    if (unapprovedWebhooks.length) {
      const data = unapprovedWebhooks[0];
      commit('setRequestedWebhook', data);
    }

    if (activeWebhooks.length) {
      const data = activeWebhooks[0];
      commit('setActiveWebhook', data);
    }
  },

  async getActiveWebhook({ state, commit }) {
    const id = state.activeWebhook.id;
    const response = await Api.ganderPortal.get(
      `apps/${state.currentWebhookId}/webhook_requests/${id}`
    );
    const webhookRequest = get(response, 'body.request');

    commit('setActiveWebhook', webhookRequest);
  },

  async getRequestedWebhook({ state, commit }) {
    const id = get(state, 'requestedWebhook.id') || state.activeWebhook.id;
    const response = await Api.ganderPortal.get(
      `apps/${state.currentWebhookId}/webhook_requests/${id}`
    );
    const resBody = get(response, 'body');

    if (resBody.request && resBody.actions) {
      commit('setRequestedWebhook', resBody.request);
      commit('setValidationActions', resBody.actions);
    }
  },

  async getWebhooksRecentActions({ state, commit }, requestId) {
    const response = await Api.ganderPortal.get(
      `apps/${state.currentWebhookId}/webhook_requests/${requestId}/actions`,
      { params: { only_recent: true } }
    );

    commit('setValidationActions', response.body);
  },

  async getErrors({ commit }) {
    if (get(state, 'activeWebhook.id')) {
      const response = await Api.ganderPortal.get(
        `apps/${state.currentWebhookId}/webhook_requests/${
          state.activeWebhook.id
        }/errors`
      );

      commit('setErrors', response.body);
    }
  },
};

const mutations = {
  setErrors(state, data) {
    state.errors = data;
  },

  setCustomMessageTypes(state, data) {
    state.customMessageTypes = data;
  },

  setRequestedWebhook(state, data) {
    state.requestedWebhook = data;
  },

  setActiveWebhook(state, data) {
    state.activeWebhook = data;
  },

  setValidationActions(state, data) {
    state.validationActions = data;
  },

  resetRequestFormErrors(state) {
    state.requestFormErrors = resetRequestFormErrors();
  },

  updateRequestFormState(state) {
    let requestForm = null;
    if (state.requestedWebhook) {
      requestForm = state.requestedWebhook;
    } else if (state.activeWebhook) {
      requestForm = state.activeWebhook;
    }

    if (requestForm) {
      const { url, auth, oauth2_client_credentials, events, batching, message_ttl } = requestForm.config;
      const eventsObj = state.requestForm.events;

      Object.keys(eventsObj).forEach(val => {
        eventsObj[val] = events.includes(val);
      });

      // Custom messages will be set always if configured for a webhook
      state.customMessageTypes.forEach(val => {
        eventsObj[val.name] = true;
      });

      const username = get(auth, 'username', '')
      const password = get(auth, 'password', '')

      state.requestForm = {
        events: eventsObj,
        url,
        authMethod: username ? 'basic_auth': 'oauth2_client_credentials',
        basicAuth: {
          username,
          password,
        },
        oauth2ClientCredentials: oauth2_client_credentials ? {
          clientId: oauth2_client_credentials.client_id,
          clientSecret: get(oauth2_client_credentials, 'client_secret', ''),
          scope: oauth2_client_credentials.scope,
          authorizationEndpoint: oauth2_client_credentials.authorization_endpoint
        } : reset().requestForm.oauth2ClientCredentials,
        batchPayloadSize: batching.max_payload_size,
        batchMaxWaitTime: batching.max_wait_time,
        messageTtl: message_ttl,
      };
    } else {
      const freshRequestForm = reset().requestForm;
      // When creating a new webhook,
      // custom messages will be set if they are configured for the app
      state.customMessageTypes.forEach(val => {
        freshRequestForm.events[val.name] = true;
      });
      state.requestForm = freshRequestForm
    }
  },

  setCurrentId(state, id) {
    state.currentWebhookId = id;
  },

  resetState(state) {
    Object.assign(state, reset());
  },
};

const getters = {
  validationErrors() {
    const errorMap = {
      auth_verification_error: 'is_auth_verified',
      app_id_verification_error: 'is_app_id_verified',
      message_delivery_verification_error: 'is_message_delivery_verified',
      ssl_verification_error: 'is_ssl_verified',
    };
    const errorKeys = Object.keys(errorMap);

    return state.validationActions.filter(actionRecord => {
      if (
        errorKeys.includes(actionRecord.action) &&
        state.requestedWebhook[errorMap[actionRecord.action]] === false
      ) {
        return true;
      }

      return false;
    });
  },

  rejectionReason() {
    const rejectionReasons = state.validationActions.filter(actionRecord => {
      if (
        actionRecord.action === 'webhook_rejected' &&
        state.requestedWebhook.is_approved === false
      ) {
        return true;
      }

      return false;
    });

    return rejectionReasons[0];
  },

  validationStatus() {
    const id = state.currentWebhookId;
    const status = {
      active: null,
      requested: null,
    };
    const keys = [
      'is_auth_verified',
      'is_app_id_verified',
      'is_message_delivery_verified',
      'is_ssl_verified',
    ];
    const validationArr = [];

    if (!id) {
      return status;
    }

    if (state.requestedWebhook) {
      const requestedValues = Object.values(pick(state.requestedWebhook, keys));

      validationArr.push(['requested', requestedValues]);
    }

    if (state.activeWebhook) {
      const activeValues = Object.values(pick(state.activeWebhook, keys));

      validationArr.push(['active', activeValues]);
    }

    validationArr.forEach(values => {
      if (
        values[1].length !== 4 ||
        values[1].includes(null) ||
        values[1].includes(undefined)
      ) {
        status[values[0]] = 'in_progress';
      } else if (values[1].every(val => val)) {
        status[values[0]] = 'success';
      } else {
        status[values[0]] = 'failure';
      }
    });

    return status;
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
